/* eslint-disable @typescript-eslint/no-unused-vars */
import { ANALYTICS_EVENT_OBJECT } from '@/constants/analytics';
import { BreadthFirstTreeProcessor } from '@skand/utils';
import { NODE_KIND } from '@/constants/node';
import { DATASET_ROOT } from '@/constants/paths';
import { canPolicyActionEdit } from '@/constants/policy';
import { queryClient } from '@/graphql/client';
import {
  AddSystemNodesToProjectMutationVariables,
  CreateProjectFileNodesWithNodeTreeMutationVariables,
  CreateProjectMutationVariables,
  ListSystemNodesByParentNodeIdQueryVariables,
  ProjectFileNode,
  QueryCountNumberOfNodesForParentNodesArgs,
  SystemNodeKindInput,
} from '@/graphql/codegen/graphql';
import {
  ADD_SYSTEM_NODES_TO_PROJECT,
  CREATE_PROJECT,
  CREATE_PROJECT_FILE_NODES_WITH_NODE_TREE,
} from '@/graphql/mutations';
import {
  COUNT_NUMBER_OF_NODES_FOR_PARENT_NODES,
  LIST_SYSTEM_NODES_BY_PARENT_NODE_ID,
} from '@/graphql/queries';
import { request } from '@/graphql/request';
import { useFetchProjectPermissions } from '@/hooks/useFetchProjectPermissions';
import { useFetchProjects } from '@/hooks/useFetchProjects';
import { resetCreateProject, useCreateProject } from '@/stores/createProject';
import { SystemNode } from '@/stores/systemNodes';
import { PaginatedProcessor } from '@skand/utils';
import { cn } from '@/utils/classname';
import { isEmpty } from '@/utils/empty';
import { ProjectGroup } from '@/utils/project';
import { createSystemNode } from '@/utils/transformers';
import { Button, Modal, toast } from '@skand/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { FileSelection } from './FileSelection';
import { InsertProjectDetails } from './InsertProjectDetails';

export interface CreateProjectProps {
  groups: ProjectGroup[];
  active: boolean;
  setActive: (value: boolean) => void;
  handleResetGroupSearchAndDialogState: () => void;
}

export const CreateProject = ({
  groups,
  active,
  setActive,
  handleResetGroupSearchAndDialogState,
}: CreateProjectProps) => {
  const [step, setStep] = useState(1);
  const [_, setSelectedNodeIds] = useState<ProjectFileNode['id'][]>([]);
  const [selectedNodes, setSelectedNodes] = useState<SystemNode[]>([]);
  const { allProjectsPermission } = useFetchProjectPermissions();
  const [addingFilesToProjectInProgress, setAddingFilesToProjectInProgress] = useState(false);

  const description = useCreateProject(state => state.description);
  const name = useCreateProject(state => state.name);
  const projectGroup = useCreateProject(state => state.projectGroup);
  const selectedGeoId = useCreateProject(state => state.selectedGeoId);
  const address = useCreateProject(state => state.address);
  const longitude = useCreateProject(state => state.longitude);
  const latitude = useCreateProject(state => state.latitude);

  const projectDetails = useMemo(() => {
    return {
      address,
      description,
      geoid: selectedGeoId.value,
      name,
      projectGroupId: projectGroup?.id ?? null,
      longitude,
      latitude,
    };
  }, [address, description, latitude, longitude, name, projectGroup?.id, selectedGeoId.value]);

  const isDisabled = useMemo(() => {
    if (step === 1) {
      // Cannot create project without group if the user doesn't have all project edit permission.
      if (projectGroup === null) {
        return !canPolicyActionEdit(allProjectsPermission);
      }
      return projectDetails.name.length === 0;
    } else if (step === 2) {
      return false;
    }
  }, [step, projectGroup, projectDetails.name, allProjectsPermission]);

  useEffect(() => {
    if (!active) setStep(1);
  }, [active]);

  const createProject = useMutation({
    mutationFn: (variables: CreateProjectMutationVariables) => {
      return request(CREATE_PROJECT, variables);
    },
  });

  const createProjectFileNode = useMutation({
    mutationFn: (variables: CreateProjectFileNodesWithNodeTreeMutationVariables) => {
      return request(CREATE_PROJECT_FILE_NODES_WITH_NODE_TREE, variables);
    },
    onSuccess: (_, { siteId }) => {
      queryClient.invalidateQueries(useFetchProjects.getProjectGroupQueryKey());
      queryClient.invalidateQueries(useFetchProjects.getProjectQueryKey());
      handleResetGroupSearchAndDialogState();
      resetCreateProject();
      window.location.href = `${DATASET_ROOT}/projects/${siteId}`;
    },
  });

  const addSystemNodesToProject = useMutation({
    mutationFn: (variables: AddSystemNodesToProjectMutationVariables) => {
      return request(ADD_SYSTEM_NODES_TO_PROJECT, variables);
    },
  });

  const renderTitle = () => {
    switch (step) {
      case 1:
        return 'Create Project';
      case 2:
        return 'Add resources to project';
    }
  };

  const renderDescription = () => {
    switch (step) {
      case 1:
        return 'Enter Details';
      case 2:
        return 'Files will be copied into the project folder';
    }
  };

  const renderContent = () => {
    switch (step) {
      case 1:
        return <InsertProjectDetails groups={groups} />;
      case 2:
        return (
          <FileSelection
            setSelectedNodeIds={setSelectedNodeIds}
            setSelectedNodes={setSelectedNodes}
          />
        );
    }
  };

  const handleCancel = () => {
    if (step === 2) {
      setStep(1);
    } else {
      setActive(false);
      resetCreateProject();
    }
  };

  const fetchSystemNodes = async (variables: ListSystemNodesByParentNodeIdQueryVariables) => {
    const result = await request(LIST_SYSTEM_NODES_BY_PARENT_NODE_ID, variables);
    return result;
  };

  const countChildOfFolders = async ({
    parentNodeIds,
  }: QueryCountNumberOfNodesForParentNodesArgs): Promise<number> => {
    const result = await request(COUNT_NUMBER_OF_NODES_FOR_PARENT_NODES, { parentNodeIds });
    return result.countNumberOfNodesForParentNodes ?? 0;
  };

  const addResourcesToProject = async (projectId: string, userFilesRootFolderNodeId: string) => {
    if (selectedNodes?.length === 0) {
      return;
    }

    setAddingFilesToProjectInProgress(true);
    toast({
      type: 'info',
      message: 'Adding files to project...',
    });

    const BATCH_SIZE = 500;
    const MAX_PARALLEL_BATCHES = 3;
    const CHILD_FETCH_PAGE_SIZE = 500;

    const nodeIdToCreatedSystemNodeIds = new Map();

    const processor = new BreadthFirstTreeProcessor({
      batchSize: BATCH_SIZE,
      maxParallelBatches: MAX_PARALLEL_BATCHES,
      getChildren: async (node: SystemNode) => {
        if (node.kind !== NODE_KIND.FOLDER_NODE) {
          return [];
        }

        const paginatedProcessor = new PaginatedProcessor<number, SystemNode[]>({
          pageSize: CHILD_FETCH_PAGE_SIZE,
          maxParallelPages: MAX_PARALLEL_BATCHES,
          getTotal: async () => {
            return await countChildOfFolders({ parentNodeIds: [node.id] });
          },
          processPage: async (_, pageIndex) => {
            const result = await fetchSystemNodes({
              parentNodeId: node.id,
              searchTerm: null,
              pageIndex,
              nodeKinds: [SystemNodeKindInput.FileNode, SystemNodeKindInput.FolderNode],
              pageSize: CHILD_FETCH_PAGE_SIZE,
              hydrateFiles: false,
            });

            const nodes: SystemNode[] = [];
            const nodeDataList = result?.listSystemNodesByParentNodeId?.data ?? [];

            nodeDataList.forEach(nodeData => {
              if (!isEmpty(nodeData)) {
                const systemNode = createSystemNode(nodeData);
                if (systemNode) {
                  nodes.push(systemNode);
                }
              }
            });

            return nodes;
          },
        });

        const allPagesData = await paginatedProcessor.processAllPages(pageIndex => pageIndex);
        return allPagesData.flat();
      },
    });

    // Use the selected nodes that were passed from FileSelection
    const initialNodes: SystemNode[] = selectedNodes;
    if (initialNodes.length > 0) {
      await processor.processSiblingBatchInLevel(
        initialNodes,
        async (siblingBatch, parent, levelIndex) => {
          const systemNodeBatchIdsToAdd = siblingBatch.map(node => node.id);

          let systemParentNodeIdToUse = null;

          if (levelIndex === 0) {
            systemParentNodeIdToUse = userFilesRootFolderNodeId;
          } else {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            systemParentNodeIdToUse = nodeIdToCreatedSystemNodeIds.get(parent!.id);
          }

          if (!systemParentNodeIdToUse) {
            throw new Error(
              `Error adding files to project: System parent node ID not found for level index ${levelIndex}`,
            );
          }

          const addFilesResult = await addSystemNodesToProject.mutateAsync({
            systemNodeIds: systemNodeBatchIdsToAdd,
            projectId,
            systemParentNodeId: systemParentNodeIdToUse,
          });

          // Store the mapping between original node IDs and the newly created node IDs
          const addedNodes = addFilesResult?.addSystemNodesToProject ?? [];

          if (addedNodes.length > 0) {
            addedNodes.forEach(mapping => {
              if (mapping && mapping.originalNodeId && mapping.createdNodeId) {
                nodeIdToCreatedSystemNodeIds.set(mapping.originalNodeId, mapping.createdNodeId);
              }
            });
          }
        },
      );
    }

    toast({
      type: 'success',
      message: `Successfully added files to project`,
      lifespan: 5000,
    });
    setAddingFilesToProjectInProgress(false);
  };

  const wrapUpProjectCreationAndNavigateToProjectDetails = (projectId: string) => {
    queryClient.invalidateQueries(useFetchProjects.getProjectGroupQueryKey());
    queryClient.invalidateQueries(useFetchProjects.getProjectQueryKey());
    handleResetGroupSearchAndDialogState();
    resetCreateProject();
    window.location.href = `${DATASET_ROOT}/projects/${projectId}`;
  };

  const handleSubmit = async () => {
    if (step === 1) {
      setStep(2);
    } else {
      const response = await createProject.mutateAsync(projectDetails);
      const projectId = response.createProject?.id ?? null;
      const userFilesRootFolderNodeId = response.createProject?.userFilesRootFolderNodeId;

      if (!userFilesRootFolderNodeId) {
        throw new Error('Error creating project: Project user files root folder node ID not found');
      }

      await addResourcesToProject(projectId as string, userFilesRootFolderNodeId as string);

      wrapUpProjectCreationAndNavigateToProjectDetails(projectId as string);
    }
  };

  const isMutating =
    createProject.isLoading || createProjectFileNode.isLoading || addSystemNodesToProject.isLoading;

  if (active)
    return (
      <Modal>
        <p className="color-neutral-800 typo-text-l">{renderTitle()}</p>
        <div
          className={cn(
            step === 1 ? 'color-neutral-800 typo-text-m ' : 'color-neutral-400 typo-text-medium',
          )}
        >
          {renderDescription()}
        </div>

        <div className={cn('flex-1 ')}>{renderContent()}</div>

        <div className={cn('flex', 'justify-end', 'gap-3')}>
          <Button
            className={cn('cursor-pointer flex-1')}
            disabled={addingFilesToProjectInProgress}
            onClick={handleCancel}
            size="s"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          <Button
            className={cn('flex-1', 'cursor-pointer')}
            data-analytics-event-object={
              step === 1
                ? ANALYTICS_EVENT_OBJECT.CREATE_PROJECT_NEXT_STEP
                : ANALYTICS_EVENT_OBJECT.CREATE_PROJECT
            }
            disabled={isDisabled || isMutating || addingFilesToProjectInProgress}
            onClick={handleSubmit}
            primary
            size="s"
          >
            {step === 1 ? 'Next' : 'Create'}
          </Button>
        </div>
      </Modal>
    );
};
